import { useState } from "react";
import { useDotLegalQuery, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";
import { useTranslation } from "@localization/useTranslation";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { useRiskAreas } from "@common/hooks/useSelectableItemQueries";
import { useFilterState } from "@common/hooks/useFilterState";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types";
import { RiskScenarioEntityTypeEnum, RiskScenariosViewModel, RiskScenarioViewModel } from "./RiskScenarios.types";
import { RiskScenarioCategory, RiskScenarioSaveModel } from "./riskScenarioDialog/RiskScenarioDialog.types";

export const riskScenarioEntityTypes = [
    RiskScenarioEntityTypeEnum.LegalEntity,
    RiskScenarioEntityTypeEnum.ProcessingActivity,
    RiskScenarioEntityTypeEnum.System,
    RiskScenarioEntityTypeEnum.Process,
    RiskScenarioEntityTypeEnum.Organisation,
];

export function getEntityTypeOptions(translateEntityType: (name: string) => string) {
    return riskScenarioEntityTypes.map((entityType) => ({ id: entityType.toString(), name: translateEntityType(RiskScenarioEntityTypeEnum[entityType]) }));
}

// What the scenario can be scored against, as one comma-separated label. The organisation is one of the types,
// so it needs no clause of its own.
export function getAppliesToLabel(
    riskScenario: RiskScenarioViewModel,
    translateString: (key: string) => string,
    translateEntityType: (name: string) => string
) {
    return riskScenario.appliesToAllEntityTypes
        ? translateString("allTypes")
        : riskScenario.entityTypes.map((entityType) => translateEntityType(RiskScenarioEntityTypeEnum[entityType])).join(", ");
}

export function toSaveModel(riskScenario: RiskScenarioViewModel): RiskScenarioSaveModel {
    return {
        id: riskScenario.id,
        name: riskScenario.name,
        description: riskScenario.description,
        category: riskScenario.category.toString(),
        riskAreaIds: riskScenario.riskAreas.map((riskArea) => riskArea.id),
        entityTypes: riskScenario.entityTypes,
    };
}

export function useRiskScenariosDataMapping() {
    const queryClient = useDotLegalQueryClient();
    const { translateString, translateRiskScenarioCategory, translateEntityType } = useTranslation();
    const { customerId } = useUserContext();
    const { riskAreas } = useRiskAreas();
    const [showRiskScenarioDialog, setShowRiskScenarioDialog] = useState(false);
    const [showCatalogDialog, setShowCatalogDialog] = useState(false);
    const [riskScenarioForDeletion, setRiskScenarioForDeletion] = useState<RiskScenarioViewModel | undefined>(undefined);
    const [selectedRiskScenario, setSelectedRiskScenario] = useState<RiskScenarioSaveModel | undefined>(undefined);

    const { selectFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } = useFilterState(
        `risk-scenarios-overview-${customerId}`
    );

    const url = "/risk-scenarios";
    const catalogUrl = "/risk-scenarios/catalog";
    const { isLoading, data } = useDotLegalQuery<RiskScenariosViewModel>(url);

    const hasRiskScenarios = isLoading || (data?.items.length ?? 0) > 0;

    const hasCatalogItemsToImport = data?.hasCatalogItemsToImport ?? false;

    const allRiskAreaIds = riskAreas?.map((riskArea) => riskArea.id) ?? [];

    const entityTypeOptions = getEntityTypeOptions(translateEntityType);
    const allEntityTypeIds = entityTypeOptions.map((option) => option.id);

    const filteredData = applyClientFilters(
        data?.items,
        [
            { id: "category", candidate: (scenario) => scenario.category.toString() },
            { id: "riskAreas", candidate: (scenario) => (scenario.appliesToAllRiskAreas ? allRiskAreaIds : scenario.riskAreas.map((riskArea) => riskArea.id)) },
            {
                id: "entityTypes",
                candidate: (scenario) =>
                    scenario.appliesToAllEntityTypes ? allEntityTypeIds : scenario.entityTypes.map((entityType) => entityType.toString()),
            },
            { id: "createdFromObservation", candidate: (scenario) => scenario.createdFromObservation.toString() },
        ],
        selectFilters,
        {},
        filterOperators
    );

    const getFilterDataOptions = (): Array<FilterOption> => {
        const categoryOptions = Object.values(RiskScenarioCategory)
            .filter((value): value is RiskScenarioCategory => typeof value === "number")
            .map((category) => ({ id: category.toString(), name: translateRiskScenarioCategory(category) }));

        return withOperators(
            [
                {
                    type: FilterOptionType.SelectField,
                    id: "category",
                    value: selectFilters.category ?? [],
                    label: translateString("category"),
                    fixedData: categoryOptions,
                },
                {
                    type: FilterOptionType.SelectField,
                    id: "riskAreas",
                    value: selectFilters.riskAreas ?? [],
                    label: translateString("riskAreas"),
                    fixedData: riskAreas ?? [],
                },
                {
                    type: FilterOptionType.SelectField,
                    id: "entityTypes",
                    value: selectFilters.entityTypes ?? [],
                    label: translateString("types"),
                    fixedData: entityTypeOptions,
                },
                {
                    type: FilterOptionType.SelectField,
                    id: "createdFromObservation",
                    value: selectFilters.createdFromObservation ?? [],
                    label: translateString("createdFromObservation"),
                    fixedData: [
                        { id: "true", name: translateString("yes") },
                        { id: "false", name: translateString("no") },
                    ],
                },
            ],
            filterOperators
        );
    };

    const onSave = () => {
        queryClient.refetchQueries(url);
        queryClient.refetchQueries(catalogUrl);
    };

    return {
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        isLoading,
        data: filteredData,
        hasRiskScenarios,
        hasCatalogItemsToImport,
        getFilterDataOptions,
        mapSearchFilter: onFilterChange,
        showRiskScenarioDialog,
        setShowRiskScenarioDialog,
        showCatalogDialog,
        setShowCatalogDialog,
        riskScenarioForDeletion,
        setRiskScenarioForDeletion,
        selectedRiskScenario,
        setSelectedRiskScenario,
        onSave,
    };
}
